import { useQuery } from "@tanstack/react-query";
import { Post } from "../../../utils/apiService";
import {
  GetAllAppointmentCommentsBody,
  GetAllApprovedVisitRequestsBody,
  GetAllBlacklistedVisitorsBody,
  GetAppointmentApprovalStatusesByIdParams,
  GetAppointmentApprovalStatusesByIdResponse,
  GetAppointmentByIdBody,
  GetCheckInDetailsById,
  GetFileByTypeBody,
} from "../interface";
import { PAGE_NAME } from "../../../constants/common";

export const FETCH_VISITOR_DETAILS_KEYS = {
  FETCH_ALL_APPROVED_VISITOR_APPOINTMENTS:
    "FETCH_ALL_APPROVED_VISITOR_APPOINTMENTS",
  FETCH_ALL_SEARCH_VISITOR_APPOINTMENTS:
    "FETCH_ALL_SEARCH_VISITOR_APPOINTMENTS",
  FETCH_ALL_VISIT_REQUEST_TYPES: "FETCH_ALL_VISIT_REQUEST_TYPES",
  FETCH_ALL_VISIT_PURPOSES: "FETCH_ALL_VISIT_PURPOSES",
  FETCH_ALL_APPOINTMENTS_COUNT: "FETCH_ALL_APPOINTMENTS_COUNT",
  FETCH_APPOINTMENT_BY_ID: "FETCH_APPOINTMENT_BY_ID",
  FETCH_APPOINTMENT_APPROVAL_STATUSES_BY_ID:
    "FETCH_APPOINTMENT_APPROVAL_STATUSES_BY_ID",
  FETCH_DEVICES_BY_ID: "FETCH_DEVICES_BY_ID",
  FETCH_ALL_APPOINTMENT_COMMENTS: "FETCH_ALL_APPOINTMENT_COMMENTS",
  FETCH_CHECK_IN_DETAILS_BY_ID: "FETCH_CHECK_IN_DETAILS_BY_ID",
  FETCH_FILE_BY_TYPE: "FETCH_FILE_BY_TYPE",
  FETCH_GET_ALL_BLACKLISTED_VISITORS: "FETCH_GET_ALL_BLACKLISTED_VISITORS",
};

const getAllBlacklistedVisitorsAction = async (
  body: GetAllBlacklistedVisitorsBody
) => {
  return Post({
    url: "/api/Blacklist/GetAll",
    body,
  });
};

export const useGetAllBlacklistedVisitors = (
  body: GetAllBlacklistedVisitorsBody
) => {
  return useQuery({
    queryKey: [
      FETCH_VISITOR_DETAILS_KEYS.FETCH_GET_ALL_BLACKLISTED_VISITORS,
      body,
    ],
    queryFn: () => getAllBlacklistedVisitorsAction(body),
  });
};

const getFileByTypeQueryAction = async (body: GetFileByTypeBody) => {
  return Post({
    url: "/api/Appointment/GetFile",
    body,
  });
};

export const useGetFileByTypeQuery = (body: GetFileByTypeBody) => {
  return useQuery({
    queryKey: [FETCH_VISITOR_DETAILS_KEYS.FETCH_FILE_BY_TYPE, body],
    queryFn: () => getFileByTypeQueryAction(body),
    enabled: !!body.Id,
  });
};

const getAllAppointmentCommentsAction = async (
  body: GetAllAppointmentCommentsBody
) => {
  return Post({
    url: "/api/Appointment/SearchComments",
    body,
  });
};

export const useGetAllAppointmentComments = (
  body: GetAllAppointmentCommentsBody
) => {
  return useQuery({
    queryKey: [FETCH_VISITOR_DETAILS_KEYS.FETCH_ALL_APPOINTMENT_COMMENTS, body],
    queryFn: () => getAllAppointmentCommentsAction(body),
    enabled: !!body.encryptedId,
  });
};

const getAllApprovedVisitRequestsAction = async (
  body: GetAllApprovedVisitRequestsBody
) => {
  return Post({
    url: "/api/Appointment/GetAllByStaff",
    body,
  });
};

export const useGetAllApprovedVisitRequests = (
  body: GetAllApprovedVisitRequestsBody,
  isEnabled: boolean
) => {
  return useQuery({
    queryKey: [
      FETCH_VISITOR_DETAILS_KEYS.FETCH_ALL_APPROVED_VISITOR_APPOINTMENTS,
      body,
    ],
    queryFn: () => getAllApprovedVisitRequestsAction(body),
    enabled: isEnabled,
  });
};

const getAllSearchVisitRequestsAction = async (
  body: GetAllApprovedVisitRequestsBody
) => {
  return Post({
    url:
      body.S_PageName === PAGE_NAME.SEARCH_APPOINTMENT_BY_STAFF
        ? "/api/Appointment/GetAll"
        : "/api/Appointment/ApproveAppointments",
    body,
  });
};

export const useGetAllSearchVisitRequests = (
  body: GetAllApprovedVisitRequestsBody
) => {
  return useQuery({
    queryKey: [
      FETCH_VISITOR_DETAILS_KEYS.FETCH_ALL_SEARCH_VISITOR_APPOINTMENTS,
      body,
    ],
    queryFn: () => getAllSearchVisitRequestsAction(body),
  });
};

const getAllVisitRequestTypesAction = async () => {
  return Post({
    url: "/api/VisitRequestTypes/GetAll",
  });
};

export const useGetAllVisitRequestTypes = () => {
  return useQuery({
    queryKey: [FETCH_VISITOR_DETAILS_KEYS.FETCH_ALL_VISIT_REQUEST_TYPES],
    queryFn: () => getAllVisitRequestTypesAction(),
  });
};

const getAppointmentByIdAction = async (body: GetAppointmentByIdBody) => {
  return Post({
    url: "/api/Appointment/GetById",
    body,
  });
};

export const useGetAppointmentById = (body: GetAppointmentByIdBody) => {
  return useQuery({
    queryKey: [
      FETCH_VISITOR_DETAILS_KEYS.FETCH_APPOINTMENT_BY_ID,
      body.encryptedId,
    ],
    queryFn: () => getAppointmentByIdAction(body),
    enabled: !!body.encryptedId,
  });
};

const getAppointmentApprovalStatusesByIdAction = async (
  params: GetAppointmentApprovalStatusesByIdParams
) => {
  return Post({
    url: "/api/Appointment/ApprovalDetails",
    params,
  });
};

export const useGetAppointmentApprovalStatusesById = (
  params: GetAppointmentApprovalStatusesByIdParams
) => {
  return useQuery<GetAppointmentApprovalStatusesByIdResponse[]>({
    queryKey: [
      FETCH_VISITOR_DETAILS_KEYS.FETCH_APPOINTMENT_APPROVAL_STATUSES_BY_ID,
      params.id,
    ],
    queryFn: () => getAppointmentApprovalStatusesByIdAction(params),
    enabled: !!params.id,
  });
};

const getCheckInDetailsByIdAction = async (body: GetCheckInDetailsById) => {
  return Post({
    url: "/api/Appointment/GetCheckInDetailsById",
    body,
  });
};

export const useGetCheckInDetailsById = (body: GetCheckInDetailsById) => {
  return useQuery({
    queryKey: [
      FETCH_VISITOR_DETAILS_KEYS.FETCH_CHECK_IN_DETAILS_BY_ID,
      body.encryptedId,
    ],
    queryFn: () => getCheckInDetailsByIdAction(body),
    enabled: !!body.encryptedId,
    retry: 1,
  });
};
